import { ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import type { RouteRecordNormalized } from 'vue-router';
import type { Breadcrumb } from '../types/breadcrumb.types';
import { CODE_TO_NAME_MAP } from '../navigation/const';
import { dynamicBreadcrumbLabel } from '../navigation/breadcrumbLabel';
import { breadcrumbTrailOverride } from '../navigation/breadcrumbTrail';

type BreadcrumbTrailMetaItem = { name: string; path?: string };

export function useBreadcrumb() {
  const route = useRoute();
  const router = useRouter();
  const breadcrumb = ref<Breadcrumb[]>([]);

  const updateBreadcrumb = () => {
    if (breadcrumbTrailOverride.value) {
      breadcrumb.value = [{ name: 'Início', path: '/' }, ...breadcrumbTrailOverride.value];
      return;
    }

    if (route.name === 'FilteredList') {
      updateInicioBreadcrumb();
    } else {
      updateDefaultBreadcrumb();
    }
  };

  const findRouteForPath = (path: string) =>
    router.getRoutes().find((candidateRoute) => matchesRoutePath(candidateRoute, path));

  const matchesRoutePath = (candidateRoute: RouteRecordNormalized, path: string): boolean => {
    if (candidateRoute.name === 'NotFound') return false;
    if (candidateRoute.path === path) return true;

    try {
      const pattern = new RegExp(`^${candidateRoute.path.replace(/:\w+/g, '[^/]+')}$`);
      return pattern.test(path);
    } catch {
      return false;
    }
  };

  const findLongestMatch = (paths: string[], consumed: number) => {
    for (let segmentCount = 1; segmentCount <= paths.length - consumed; segmentCount++) {
      const candidatePath = `/${paths.slice(0, consumed + segmentCount).join('/')}`;
      const matchedRoute = findRouteForPath(candidatePath);

      if (matchedRoute) return { matchedRoute, segmentCount };
    }

    return null;
  };

  const getMatchedRouteName = (
    matchedRoute: RouteRecordNormalized,
    fallbackSegment: string
  ): string => {
    if (matchedRoute.meta.breadcrumb && typeof matchedRoute.meta.breadcrumb === 'string') {
      return matchedRoute.meta.breadcrumb;
    }

    return matchedRoute.name?.toString() || fallbackSegment;
  };

  const capitalize = (segment: string): string => segment.charAt(0).toUpperCase() + segment.slice(1);

  const applyDynamicBreadcrumbOverride = (breadcrumbItems: Breadcrumb[]): void => {
    const lastItem = breadcrumbItems[breadcrumbItems.length - 1];
    if (!route.meta?.dynamicBreadcrumb || !dynamicBreadcrumbLabel.value || !lastItem) return;

    lastItem.name = dynamicBreadcrumbLabel.value;
  };

  const resolveBreadcrumbTrail = (): Breadcrumb[] | null => {
    const trail = route.meta?.breadcrumbTrail as BreadcrumbTrailMetaItem[] | undefined;
    if (!Array.isArray(trail) || trail.length === 0) return null;

    return trail.map((item) => ({ name: item.name, path: item.path ?? route.path }));
  };

  const buildBreadcrumbFromPath = (): Breadcrumb[] => {
    const paths = route.path.split('/').filter((segment) => segment);
    const breadcrumbItems: Breadcrumb[] = [];

    let consumed = 0;

    while (consumed < paths.length) {
      const match = findLongestMatch(paths, consumed);
      const endIndex = consumed + (match?.segmentCount ?? 1);
      const currentPath = `/${paths.slice(0, endIndex).join('/')}`;
      const fallbackSegment = paths[endIndex - 1];

      breadcrumbItems.push({
        name: match
          ? getMatchedRouteName(match.matchedRoute, fallbackSegment)
          : capitalize(fallbackSegment),
        path: currentPath,
      });

      consumed = endIndex;
    }

    return breadcrumbItems;
  };

  const updateDefaultBreadcrumb = () => {
    const breadcrumbItems: Breadcrumb[] = [
      { name: 'Início', path: '/' },
      ...(resolveBreadcrumbTrail() ?? buildBreadcrumbFromPath()),
    ];

    applyDynamicBreadcrumbOverride(breadcrumbItems);
    breadcrumb.value = breadcrumbItems;
  };

  const updateInicioBreadcrumb = () => {
    const breadcrumbItems: Breadcrumb[] = [{ name: 'Início', path: '/' }];

    const itemPath = route.params.itemName as string;
    if (!itemPath) {
      breadcrumb.value = breadcrumbItems;
      return;
    }

    const pathString = Array.isArray(itemPath) ? itemPath.join('-') : String(itemPath);
    const segments = pathString.split('-');

    segments.forEach((segment, i) => {
      const isLast = i === segments.length - 1;

      const displayName = getDisplayName(segment);
      const currentPath = segments.slice(0, i + 1).join('-');

      breadcrumbItems.push({
        name: displayName,
        path: isLast ? route.path : `/${currentPath}`,
        clickable: isLast,
      });
    });

    breadcrumb.value = breadcrumbItems;
  };

  const getDisplayName = (segment: string): string => {
    if (segment.startsWith('M') && segment.includes('-')) {
      const [moduleCode, categoryCode] = segment.split('-');
      const moduleName = CODE_TO_NAME_MAP[moduleCode] || moduleCode;
      const categoryName = CODE_TO_NAME_MAP[categoryCode] || categoryCode;
      return `${moduleName} - ${categoryName}`;
    }

    if (segment.startsWith('M') && /^\d+$/.test(segment.slice(1))) {
      return 'Módulo';
    }

    return CODE_TO_NAME_MAP[segment] || segment;
  };

  return {
    updateBreadcrumb,
    breadcrumb,
  };
}
